// mission-settings-reapply-from-pinned-pack.ts — review 04b L6 (spec V2 E10,
// §7 "Config ↔ CLI": mọi đường re-apply settings PHẢI cập nhật
// `compatibility.json.settings_digest` cùng lúc).
//
// Dựng lại `.claude/settings.json` của mission (đường drift
// `permissions_allow_fallback` của supervisor-config-drift-reconcile.ts) rồi
// ghi digest mới vào compatibility.json TRONG CÙNG bracket lock settings — nếu
// không, doctor báo "settings mission BỊ SỬA ngoài supervisor".
//  - Hooks dựng từ pack ĐÃ GHIM trong mission (không cài lại pack: pack bất
//    biến suốt mission), không tạo worktree. Trước đây đường này gọi
//    `createMissionWorkspace` với source repo giả ⇒ luôn lỗi và bị nuốt.
//  - Profile tool (`avatar-tools.json`) GIỮ NGUYÊN, chỉ đọc để tính digest
//    (cùng công thức init — computeSettingsProfileDigest). Đọc không được ⇒
//    ném TRƯỚC khi ghi bất cứ gì (không ghi nửa vời).
import { join } from "node:path";
import { readJson, writeJsonAtomic } from "../filesystem-helpers.js";
import { withSettingsFileLock } from "../with-settings-file-lock.js";
import { writeFileDurable } from "./durable-file-writer.js";
import { missionLayoutFromWorkspace } from "./mission-resolver.js";
import {
  buildMissionSettings,
  computeSettingsProfileDigest,
  type ToolStateFile,
} from "./mission-settings-profile.js";
import { buildMissionHooks } from "./mission-workspace-hooks.js";
import { resolveSourceGitDir, resolveSourceRepoRoot } from "./source-git-dir-resolver.js";
import { canonicalWorktreePath } from "./worktree-bound-tool-rules.js";

export interface ReapplyMissionSettingsInput {
  controlRoot: string;
  missionWorkspace: string;
  permissionsAllowFallback: boolean;
}

/** Ghi settings mới + `settings_digest`/`profile_digest` mới; trả digest. */
export async function reapplyMissionSettingsAndDigest(
  input: ReapplyMissionSettingsInput,
): Promise<string> {
  const claudeDir = join(input.missionWorkspace, ".claude");
  const compatPath = join(input.controlRoot, "compatibility.json");
  const toolState = await readJson<ToolStateFile>(join(claudeDir, "avatar-tools.json"));
  const compat = await readJson<Record<string, unknown>>(compatPath);
  // Re-audit 06g N7: the same four bindings `createMissionWorkspace` wrote.
  const layout = missionLayoutFromWorkspace(input.missionWorkspace);
  const settings = buildMissionSettings({
    permissionsAllowFallback: input.permissionsAllowFallback,
    sourceGitDir: await resolveSourceGitDir(input.missionWorkspace),
    sourceRepo: await resolveSourceRepoRoot(input.missionWorkspace),
    controlRoot: canonicalWorktreePath(input.controlRoot),
    workspaceRoot: canonicalWorktreePath(layout.workspaceRoot),
    hooks: await buildMissionHooks(join(claudeDir, "pack")),
  });
  const digest = computeSettingsProfileDigest(settings, toolState);
  const nextCompat = { ...compat, settings_digest: digest, profile_digest: digest };
  await withSettingsFileLock(input.missionWorkspace, async () => {
    await writeJsonAtomic(join(claudeDir, "settings.json"), settings, 0o600);
    await writeFileDurable(
      compatPath,
      Buffer.from(`${JSON.stringify(nextCompat, null, 2)}\n`, "utf8"),
    );
  });
  return digest;
}
